import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  MessageCircle,
  Video,
  Phone,
  MapPin,
  Star,
  ChevronRight,
  Send,
  Check,
  Plus,
  Minus,
} from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { api, ASSET_ICON } from "@/lib/api";
import { pickLangText } from "@/i18n";
import {
  zodiacSigns,
  whyChooseCopy,
  problemAreasCopy,
  servicesOfferedCopy,
  faqCopy,
  gallery,
  sectionLabelsCopy,
} from "@/content";

const Home = ({ packages, settings, feedback }) => {
  const { t, lang } = useLang();
  const navigate = useNavigate();

  const [fbForm, setFbForm] = useState({ name: "", city: "", text: "" });
  const [fbBusy, setFbBusy] = useState(false);
  const [fbMsg, setFbMsg] = useState(null);

  const wa = settings?.whatsapp_number || "919462550555";
  const inquiry = settings?.inquiry_number || "919462029820";
  const office =
    lang === "hi"
      ? settings?.office_address_hi ||
        "अत्रेय सदन, 53, जगतपुरा, कशिश-1 अपार्टमेंट्स के पास, कारोलों का बाड़, जयपुर, राजस्थान 302017, भारत"
      : settings?.office_address_en ||
        "Atrey Sadan, 53, Jagatpura, near Kashish 1 Apartments, Karolan Ka Barh, Jaipur, Rajasthan 302017, India";

  // Prefer the exact Google Maps share URL provided by the astrologer;
  // fall back to a search query built from the postal address.
  const mapsUrl =
    settings?.google_maps_url ||
    `https://maps.google.com/?q=${encodeURIComponent(office)}`;
  // Embed uses the verified business name so Google shows the correct pin.
  const mapsEmbedUrl =
    "https://maps.google.com/maps?q=" +
    encodeURIComponent("Devender Sharma Astrologer, Jagatpura, Jaipur") +
    "&z=16&output=embed";

  const localized = (pkg) => ({
    title: pickLangText(pkg, lang, "title"),
    subtitle: pickLangText(pkg, lang, "subtitle"),
    desc: pickLangText(pkg, lang, "desc"),
  });

  const submitFeedback = async (e) => {
    e.preventDefault();
    if (!fbForm.name || !fbForm.text) return;
    setFbBusy(true);
    setFbMsg(null);
    try {
      await api.post("/feedback", {
        name: fbForm.name,
        city: fbForm.city,
        text: fbForm.text,
      });
      setFbMsg({ type: "ok", text: t.feedbackThanks });
      setFbForm({ name: "", city: "", text: "" });
    } catch (err) {
      setFbMsg({ type: "err", text: err?.response?.data?.detail || "Error" });
    } finally {
      setFbBusy(false);
    }
  };

  return (
    <div className={lang === "hi" ? "font-devnagri" : ""}>
      {/* ---------------- Hero ---------------- */}
      <section className="section pt-16 md:pt-24 relative overflow-hidden" id="home">
        <div className="rays" aria-hidden />
        <div className="container-narrow relative z-10 grid md:grid-cols-2 items-center gap-12">
          <div className="reveal">
            <span className="gold-divider mb-6">{t.heroBadge}</span>

            <h1
              className="font-display text-4xl md:text-6xl leading-[1.05] mb-4"
              style={{ color: "#f6ecd6" }}
              data-testid="hero-name"
            >
              {t.astrologerName}
            </h1>
            <p className="font-sans-ui text-sm md:text-base tracking-wider uppercase text-[color:var(--gold-soft)] mb-6">
              {t.astrologerTitle}
            </p>

            <div
              className="inline-block mb-8 px-5 py-2 rounded-full"
              style={{
                background:
                  "linear-gradient(135deg, rgba(212,166,74,0.14), rgba(212,166,74,0.05))",
                border: "1px solid rgba(212,166,74,0.5)",
              }}
              data-testid="hero-tagline"
            >
              <span className="gold-text font-display text-xl md:text-2xl">
                {t.tagline}
              </span>
            </div>

            <p className="text-[color:var(--cream)] opacity-90 max-w-lg mb-6 text-lg">
              {t.heroSub}
            </p>

            <div className="flex flex-wrap gap-3 mb-8">
              <span className="chip">★ {t.heroChip1}</span>
              <span className="chip">✦ {t.heroChip2}</span>
            </div>

            <div className="flex flex-wrap gap-3">
              <Link
                to="/booking"
                className="btn-gold"
                data-testid="hero-book-btn"
              >
                {t.bookNow} <ChevronRight size={16} />
              </Link>
              <a
                href={`https://wa.me/+${wa}`}
                target="_blank"
                rel="noreferrer"
                className="btn-ghost"
                data-testid="hero-wa-btn"
              >
                <MessageCircle size={16} /> WhatsApp
              </a>
            </div>

            <div className="mt-10">
              <div className="text-xs uppercase tracking-[0.3em] text-[color:var(--gold-soft)] opacity-80 mb-3">
                {t.expertiseTitle}
              </div>
              <div className="flex flex-wrap gap-2">
                {t.expertise.map((e) => (
                  <span
                    key={e}
                    className="px-3 py-1 rounded-full text-xs font-sans-ui border border-[rgba(212,166,74,0.4)] text-[color:var(--cream)]"
                  >
                    {e}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Right visual */}
          <div className="relative reveal" style={{ animationDelay: ".15s" }}>
            <div
              className="relative mx-auto"
              style={{ width: "min(420px, 100%)" }}
            >
              <div
                className="absolute inset-0 rounded-3xl"
                style={{
                  background:
                    "radial-gradient(circle at 50% 40%, rgba(212,166,74,0.35), transparent 60%)",
                  filter: "blur(30px)",
                }}
              />
              <img
                src={ASSET_ICON}
                alt="AstroDev icon"
                className="relative w-full rounded-[28px] icon-halo"
                style={{
                  boxShadow:
                    "0 30px 80px -30px rgba(212,166,74,0.55), 0 0 0 1px rgba(212,166,74,0.35) inset",
                }}
                data-testid="hero-icon"
              />
            </div>
          </div>
        </div>

        {/* Marquee */}
        <div className="marquee mt-16 border-y border-[rgba(212,166,74,0.2)] py-4 relative z-10">
          <div className="marquee-track">
            {[
              "Vedic Jyotish",
              "KP Paddhati",
              "Cuspal Interlink",
              "Lal Kitab",
              "Ashtakvarga",
              "Jaimini",
              "Prashna",
              "Nakshatra",
              "Muhurta",
              "Vedic Jyotish",
              "KP Paddhati",
              "Cuspal Interlink",
              "Lal Kitab",
              "Ashtakvarga",
              "Jaimini",
              "Prashna",
              "Nakshatra",
              "Muhurta",
            ].map((v, i) => (
              <span key={i}>✦&nbsp;&nbsp;{v}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- Cherished Moment (featured photo) ---------------- */}
      <section className="section pt-4 md:pt-10" id="moment">
        <div className="container-narrow">
          <div className="card-glass p-6 md:p-10 grid md:grid-cols-5 gap-8 md:gap-10 items-center reveal">
            <div className="md:col-span-3 relative">
              <div
                className="absolute -inset-6 rounded-3xl"
                style={{
                  background:
                    "radial-gradient(circle at 50% 50%, rgba(212,166,74,0.28), transparent 65%)",
                  filter: "blur(30px)",
                  pointerEvents: "none",
                }}
              />
              <img
                src="/featured-moment.png"
                alt="Presenting Hon'ble Dr. Justice D. Y. Chandrachud, Former Chief Justice of India, with a framed portrait as a mark of respect."
                loading="lazy"
                className="relative w-full rounded-2xl"
                style={{
                  boxShadow:
                    "0 30px 80px -30px rgba(0,0,0,0.7), 0 0 0 1px rgba(212,166,74,0.4)",
                }}
                data-testid="featured-moment-img"
              />
              <p
                className="mt-3 text-center text-xs md:text-sm tracking-wide text-[color:var(--gold-soft)] font-sans-ui"
                data-testid="featured-moment-credit"
              >
                {sectionLabelsCopy[lang].momentCredit}
              </p>
            </div>
            <div className="md:col-span-2">
              <span className="gold-divider mb-4">Moment</span>
              <h2
                className="font-display text-3xl md:text-4xl mb-4"
                style={{ color: "#f6ecd6" }}
              >
                {sectionLabelsCopy[lang].momentTitle}
              </h2>
              <p className="text-[color:var(--cream)] opacity-90 mb-6 leading-relaxed">
                {sectionLabelsCopy[lang].momentSub}
              </p>
              <blockquote
                className="italic text-[color:var(--gold-soft)] border-l-2 pl-4"
                style={{ borderColor: "var(--gold)" }}
              >
                {sectionLabelsCopy[lang].momentQuote}
              </blockquote>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- Zodiac ---------------- */}
      <section className="section" id="zodiac">
        <div className="container-narrow">
          <div className="text-center mb-10 reveal">
            <span className="gold-divider mb-4">Rashi</span>
            <h2 className="font-display text-3xl md:text-5xl gold-text mb-3">
              {sectionLabelsCopy[lang].zodiacTitle}
            </h2>
            <p className="text-[color:var(--cream)] opacity-80 max-w-xl mx-auto">
              {sectionLabelsCopy[lang].zodiacSub}
            </p>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3 md:gap-4">
            {zodiacSigns.map((z, i) => (
              <button
                key={z.en}
                type="button"
                onClick={() => navigate("/booking")}
                className="card-glass p-4 flex flex-col items-center text-center gap-1 group"
                data-testid={`zodiac-${z.en.toLowerCase()}`}
                style={{ animationDelay: `${i * 30}ms` }}
              >
                <span
                  className="text-3xl md:text-4xl"
                  style={{ color: "var(--gold)" }}
                >
                  {z.glyph}
                </span>
                <span
                  className="font-display text-base md:text-lg"
                  style={{ color: "#f6ecd6" }}
                >
                  {lang === "hi" ? z.hi : z.en}
                </span>
                <span className="text-[10px] md:text-[11px] tracking-widest uppercase text-[color:var(--gold-soft)] font-sans-ui opacity-90">
                  {z.range}
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- Packages ---------------- */}
      <section className="section" id="packages">
        <div className="container-narrow">
          <div className="text-center mb-14 reveal">
            <span className="gold-divider mb-5">Consultation Packages</span>
            <h2 className="font-display text-4xl md:text-5xl gold-text mb-3">
              {t.packagesTitle}
            </h2>
            <p className="text-[color:var(--cream)] opacity-80 max-w-2xl mx-auto">
              {t.packagesSub}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {(packages || []).map((p) => {
              const info = localized(p);
              return (
                <div
                  key={p.id}
                  className="card-glass p-7 relative flex flex-col"
                  data-testid={`package-card-${p.id}`}
                >
                  <div className="text-xs tracking-[0.3em] uppercase text-[color:var(--gold-soft)] mb-3">
                    #{String(p.order / 100).padStart(2, "0")}
                  </div>
                  <h3
                    className="font-display text-2xl mb-1"
                    style={{ color: "#f6ecd6" }}
                  >
                    {info.title}
                  </h3>
                  <p className="text-[color:var(--gold-soft)] text-sm mb-4 font-sans-ui">
                    {info.subtitle}
                  </p>
                  <p className="text-[color:var(--cream)] opacity-85 text-sm leading-relaxed flex-1">
                    {info.desc}
                  </p>

                  <div className="mt-6 flex items-center justify-between">
                    <div className="price-tag">
                      <span className="rupee">₹</span>
                      <span className="amount">{p.amount}</span>
                    </div>
                    <button
                      onClick={() =>
                        navigate("/booking", { state: { pkg: p } })
                      }
                      className="btn-gold text-sm py-2 px-4"
                      data-testid={`book-${p.id}`}
                    >
                      {t.bookPackage}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ---------------- Services Offered ---------------- */}
      <section className="section" id="services">
        <div className="container-narrow">
          <div className="text-center mb-12 reveal">
            <span className="gold-divider mb-4">Services</span>
            <h2 className="font-display text-3xl md:text-5xl gold-text mb-3">
              {servicesOfferedCopy[lang].title}
            </h2>
            <p className="text-[color:var(--cream)] opacity-80 max-w-2xl mx-auto">
              {servicesOfferedCopy[lang].sub}
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {servicesOfferedCopy[lang].items.map((s, i) => (
              <div
                key={i}
                className="card-glass p-6"
                data-testid={`service-${i}`}
              >
                <div
                  className="text-xs tracking-[0.3em] uppercase text-[color:var(--gold-soft)] mb-2"
                >
                  0{i + 1}
                </div>
                <h3
                  className="font-display text-xl mb-4"
                  style={{ color: "#f6ecd6" }}
                >
                  {s.title}
                </h3>
                <ul className="space-y-2">
                  {s.points.map((pt, k) => (
                    <li
                      key={k}
                      className="flex gap-2 text-[color:var(--cream)] opacity-90 text-sm leading-relaxed"
                    >
                      <Check
                        size={16}
                        className="text-[color:var(--gold)] shrink-0 mt-0.5"
                      />
                      <span>{pt}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- About ---------------- */}
      <section className="section" id="about">
        <div className="container-narrow grid md:grid-cols-5 gap-10 items-start">
          <div className="md:col-span-2 reveal">
            <div className="card-glass p-8">
              <div
                className="relative mx-auto mb-6"
                style={{ maxWidth: 280 }}
              >
                <div
                  className="absolute -inset-4 rounded-3xl"
                  style={{
                    background:
                      "radial-gradient(circle at 50% 40%, rgba(212,166,74,0.28), transparent 65%)",
                    filter: "blur(24px)",
                    pointerEvents: "none",
                  }}
                />
                <img
                  src="/portrait.jpg"
                  alt="Devender Parkash Sharma — Astrologer"
                  loading="lazy"
                  className="relative w-full rounded-2xl object-cover aspect-[4/5]"
                  style={{
                    boxShadow:
                      "0 22px 60px -22px rgba(0,0,0,0.7), 0 0 0 1px rgba(212,166,74,0.4)",
                    objectPosition: "center 20%",
                  }}
                  data-testid="about-portrait"
                />
              </div>
              <div
                className="text-4xl gold-text font-display mb-3 text-center"
                style={{ letterSpacing: "0.08em" }}
              >
                ॐ
              </div>
              <div className="hairline mb-4" />
              <h3
                className="font-display text-xl md:text-2xl mb-2 text-center"
                style={{ color: "#f6ecd6" }}
              >
                {t.astrologerName}
              </h3>
              <p className="text-center text-xs text-[color:var(--gold-soft)] mb-5 font-sans-ui tracking-wide">
                {t.aboutRole}
              </p>
              <blockquote className="text-center italic text-[color:var(--cream)] opacity-90 text-sm">
                “{t.aboutQuote}”
              </blockquote>
            </div>
          </div>

          <div className="md:col-span-3 reveal" style={{ animationDelay: ".1s" }}>
            <span className="gold-divider mb-4">{t.aboutTitle}</span>
            <h2 className="font-display text-3xl md:text-4xl mb-6" style={{ color: "#f6ecd6" }}>
              {t.aboutTitle}
            </h2>
            <div className="space-y-5 text-[color:var(--cream)] opacity-90 leading-relaxed">
              <p>{t.aboutP1}</p>
              <p>{t.aboutP2}</p>
              <p>{t.aboutP3}</p>
              <p>{t.aboutP4}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- Why Choose + Problem Areas ---------------- */}
      <section className="section" id="why">
        <div className="container-narrow grid lg:grid-cols-2 gap-8">
          <div className="card-glass p-7 md:p-8" data-testid="why-choose">
            <span className="gold-divider mb-4">Trust</span>
            <h3
              className="font-display text-2xl md:text-3xl mb-3"
              style={{ color: "#f6ecd6" }}
            >
              {whyChooseCopy[lang].title}
            </h3>
            <p className="text-[color:var(--cream)] opacity-80 mb-6">
              {whyChooseCopy[lang].sub}
            </p>
            <ul className="space-y-3">
              {whyChooseCopy[lang].items.map((it, i) => (
                <li
                  key={i}
                  className="flex gap-3 text-[color:var(--cream)] opacity-95"
                >
                  <span
                    className="w-6 h-6 rounded-full inline-flex items-center justify-center border border-[color:var(--gold)] text-[color:var(--gold)] shrink-0 mt-0.5"
                    style={{ background: "rgba(212,166,74,0.08)" }}
                  >
                    <Check size={12} />
                  </span>
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="card-glass p-7 md:p-8" data-testid="problem-areas">
            <span className="gold-divider mb-4">Areas</span>
            <h3
              className="font-display text-2xl md:text-3xl mb-3"
              style={{ color: "#f6ecd6" }}
            >
              {problemAreasCopy[lang].title}
            </h3>
            <p className="text-[color:var(--cream)] opacity-80 mb-6">
              {problemAreasCopy[lang].sub}
            </p>
            <ul className="grid gap-3">
              {problemAreasCopy[lang].items.map((it, i) => (
                <li
                  key={i}
                  className="flex gap-3 text-[color:var(--cream)] opacity-95"
                >
                  <span
                    className="text-[color:var(--gold)] shrink-0 mt-0.5"
                    aria-hidden
                  >
                    ✦
                  </span>
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ---------------- Testimonials ---------------- */}
      <section className="section" id="testimonials">
        <div className="container-narrow">
          <div className="text-center mb-12">
            <span className="gold-divider mb-4">Testimonials</span>
            <h2 className="font-display text-4xl md:text-5xl gold-text">
              {t.testimonialsTitle}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {t.testimonials.map((tst, i) => (
              <div key={i} className="card-glass p-6" data-testid={`testimonial-${i}`}>
                <div className="flex items-center gap-1 mb-3 text-[color:var(--gold)]">
                  {Array.from({ length: 5 }).map((_, k) => (
                    <Star key={k} size={14} fill="currentColor" strokeWidth={0} />
                  ))}
                </div>
                <p className="text-[color:var(--cream)] opacity-90 leading-relaxed mb-4">
                  “{tst.text}”
                </p>
                <p className="text-sm text-[color:var(--gold-soft)] font-sans-ui">
                  {tst.author}
                </p>
              </div>
            ))}

            {(feedback || []).slice(0, 6).map((f, i) => (
              <div
                key={f.id || `fb-${i}`}
                className="card-glass p-6"
                data-testid={`feedback-${f.id || i}`}
              >
                <div className="flex items-center gap-1 mb-3 text-[color:var(--gold)]">
                  {Array.from({ length: 5 }).map((_, k) => (
                    <Star key={k} size={14} fill="currentColor" strokeWidth={0} />
                  ))}
                </div>
                <p className="text-[color:var(--cream)] opacity-90 leading-relaxed mb-4">
                  “{f.text}”
                </p>
                <p className="text-sm text-[color:var(--gold-soft)] font-sans-ui">
                  — {f.name}
                  {f.city ? `, ${f.city}` : ""}
                </p>
              </div>
            ))}
          </div>

          {/* Feedback form */}
          {settings?.feedback_enabled !== false && (
            <div className="card-glass p-8 mt-10 max-w-2xl mx-auto" data-testid="feedback-form">
              <h3 className="font-display text-2xl gold-text mb-4">
                {t.addExperience}
              </h3>
              <form onSubmit={submitFeedback} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="field">
                    <label>{t.feedbackName}</label>
                    <input
                      className="input"
                      value={fbForm.name}
                      onChange={(e) =>
                        setFbForm({ ...fbForm, name: e.target.value })
                      }
                      required
                      data-testid="feedback-name"
                    />
                  </div>
                  <div className="field">
                    <label>{t.feedbackCity}</label>
                    <input
                      className="input"
                      value={fbForm.city}
                      onChange={(e) =>
                        setFbForm({ ...fbForm, city: e.target.value })
                      }
                      data-testid="feedback-city"
                    />
                  </div>
                </div>
                <div className="field">
                  <label>{t.feedbackText}</label>
                  <textarea
                    className="textarea"
                    value={fbForm.text}
                    onChange={(e) =>
                      setFbForm({ ...fbForm, text: e.target.value })
                    }
                    required
                    data-testid="feedback-text"
                  />
                </div>
                {fbMsg && (
                  <div
                    className={`notice ${fbMsg.type === "err" ? "error" : ""}`}
                    data-testid="feedback-message"
                  >
                    {fbMsg.text}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={fbBusy}
                  className="btn-gold w-full sm:w-auto"
                  data-testid="feedback-submit"
                >
                  <Send size={14} /> {fbBusy ? t.submitting : t.submitFeedback}
                </button>
              </form>
            </div>
          )}
        </div>
      </section>

      {/* ---------------- Gallery ---------------- */}
      {gallery.length > 0 && (
      <section className="section" id="gallery">
        <div className="container-narrow">
          <div className="text-center mb-10 reveal">
            <span className="gold-divider mb-4">Gallery</span>
            <h2 className="font-display text-3xl md:text-5xl gold-text mb-3">
              {sectionLabelsCopy[lang].galleryTitle}
            </h2>
            <p className="text-[color:var(--cream)] opacity-80 max-w-xl mx-auto">
              {sectionLabelsCopy[lang].gallerySub}
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {gallery.map((src, i) => (
              <GalleryTile key={i} src={src} index={i} />
            ))}
          </div>
        </div>
      </section>
      )}

      {/* ---------------- FAQ ---------------- */}
      <FAQSection lang={lang} />

      {/* ---------------- Contact Modes ---------------- */}
      <section className="section" id="contact">
        <div className="container-narrow">
          <div className="text-center mb-12">
            <span className="gold-divider mb-4">Reach us</span>
            <h2 className="font-display text-4xl md:text-5xl gold-text">
              {t.contactModesTitle}
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            <a
              href={`https://wa.me/+${wa}`}
              target="_blank"
              rel="noreferrer"
              className="card-glass p-6 block"
              data-testid="mode-wa-chat"
            >
              <MessageCircle className="text-[color:var(--gold)] mb-3" />
              <div className="font-display text-lg mb-1" style={{ color: "#f6ecd6" }}>
                {t.modeWaChat}
              </div>
              <div className="text-sm text-[color:var(--gold-soft)] font-sans-ui">
                +{wa}
              </div>
            </a>

            <a
              href={`https://wa.me/+${wa}?text=Video%20call%20booking`}
              target="_blank"
              rel="noreferrer"
              className="card-glass p-6 block"
              data-testid="mode-wa-video"
            >
              <Video className="text-[color:var(--gold)] mb-3" />
              <div className="font-display text-lg mb-1" style={{ color: "#f6ecd6" }}>
                {t.modeWaVideo}
              </div>
              <div className="text-sm text-[color:var(--cream)] opacity-80 font-sans-ui">
                {t.modeWaVideoDesc}
              </div>
            </a>

            <a
              href={`tel:+${wa}`}
              className="card-glass p-6 block"
              data-testid="mode-voice"
            >
              <Phone className="text-[color:var(--gold)] mb-3" />
              <div className="font-display text-lg mb-1" style={{ color: "#f6ecd6" }}>
                {t.modeVoice}
              </div>
              <div className="text-sm text-[color:var(--gold-soft)] font-sans-ui">
                +{wa}
              </div>
            </a>

            <div className="card-glass p-6" data-testid="mode-in-person">
              <MapPin className="text-[color:var(--gold)] mb-3" />
              <div className="font-display text-lg mb-1" style={{ color: "#f6ecd6" }}>
                {t.modeInPerson}
              </div>
              <div className="text-sm text-[color:var(--cream)] opacity-80 font-sans-ui">
                {t.modeInPersonDesc}
              </div>
            </div>
          </div>

          {/* Office */}
          <div className="card-glass p-8 mt-10 grid md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="text-xs tracking-[0.3em] uppercase text-[color:var(--gold-soft)] mb-3">
                {t.officeTitle}
              </div>
              <div className="font-display text-2xl mb-2" style={{ color: "#f6ecd6" }}>
                {t.officeName}
              </div>
              <p className="text-[color:var(--cream)] opacity-90 mb-5 leading-relaxed">
                {office}
              </p>
              <p className="text-sm text-[color:var(--gold-soft)] italic mb-5">
                {t.inPersonNote}
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href={`tel:+${wa}`}
                  className="btn-gold text-sm py-2 px-4"
                  data-testid="office-call-btn"
                >
                  <Phone size={14} /> {t.callNow}
                </a>
                <a
                  href={mapsUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-ghost text-sm py-2 px-4"
                  data-testid="office-maps-btn"
                >
                  <MapPin size={14} /> {t.openInMaps}
                </a>
              </div>
            </div>

            <div
              className="rounded-2xl overflow-hidden border border-[rgba(212,166,74,0.35)] h-64 md:h-72 relative"
              data-testid="office-map-embed"
            >
              <iframe
                title="AstroDev office map"
                width="100%"
                height="100%"
                loading="lazy"
                style={{ border: 0 }}
                src={mapsEmbedUrl}
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-5 mt-8">
            <div className="card-glass p-6 flex items-center justify-between">
              <div>
                <div className="text-xs tracking-[0.3em] uppercase text-[color:var(--gold-soft)] mb-1">
                  {t.whatsappVoice}
                </div>
                <div className="font-display text-xl" style={{ color: "#f6ecd6" }}>
                  +{wa.replace(/^91/, "+91 ").replace(/^\+91 /, "91 ")}
                </div>
              </div>
              <a
                href={`https://wa.me/+${wa}`}
                target="_blank"
                rel="noreferrer"
                className="btn-ghost text-sm py-2 px-4"
                data-testid="contact-wa-btn"
              >
                WhatsApp
              </a>
            </div>
            <div className="card-glass p-6 flex items-center justify-between">
              <div>
                <div className="text-xs tracking-[0.3em] uppercase text-[color:var(--gold-soft)] mb-1">
                  {t.inquiryPayment}
                </div>
                <div className="font-display text-xl" style={{ color: "#f6ecd6" }}>
                  +91 {inquiry.replace(/^91/, "")}
                </div>
              </div>
              <a
                href={`tel:+${inquiry}`}
                className="btn-ghost text-sm py-2 px-4"
                data-testid="contact-call-btn"
              >
                <Phone size={14} /> {t.callNow}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// ---------------- FAQ (accordion) ----------------
const FAQSection = ({ lang }) => {
  const data = faqCopy[lang];
  const [open, setOpen] = useState(0);
  return (
    <section className="section" id="faq">
      <div className="container-narrow max-w-3xl">
        <div className="text-center mb-10">
          <span className="gold-divider mb-4">FAQ</span>
          <h2 className="font-display text-3xl md:text-5xl gold-text">
            {data.title}
          </h2>
        </div>
        <div className="space-y-3">
          {data.items.map((it, i) => {
            const isOpen = open === i;
            return (
              <div
                key={i}
                className="card-glass"
                data-testid={`faq-item-${i}`}
                style={{ padding: 0 }}
              >
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  className="w-full flex items-center justify-between gap-4 text-left px-5 py-4"
                  data-testid={`faq-toggle-${i}`}
                  aria-expanded={isOpen}
                >
                  <span
                    className="font-display text-base md:text-lg"
                    style={{ color: "#f6ecd6" }}
                  >
                    {it.q}
                  </span>
                  <span
                    className="w-8 h-8 rounded-full inline-flex items-center justify-center border border-[color:var(--gold)] text-[color:var(--gold)] shrink-0"
                    style={{ background: "rgba(212,166,74,0.08)" }}
                  >
                    {isOpen ? <Minus size={14} /> : <Plus size={14} />}
                  </span>
                </button>
                {isOpen && (
                  <div
                    className="px-5 pb-5 pt-0 text-[color:var(--cream)] opacity-90 leading-relaxed"
                    data-testid={`faq-answer-${i}`}
                  >
                    <div className="hairline mb-4" />
                    {it.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Home;

// Gallery tile that hides itself if the image fails (avoids hotlink-blocked blanks)
function GalleryTile({ src, index, onLoadStatus }) {
  const [ok, setOk] = useState(true);
  if (!ok) return null;
  return (
    <div
      className="relative overflow-hidden rounded-xl border border-[rgba(212,166,74,0.35)] aspect-[4/3] group"
      data-testid={`gallery-item-${index}`}
    >
      <img
        src={src}
        alt="AstroDev gallery"
        loading="lazy"
        referrerPolicy="no-referrer"
        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        onLoad={() => onLoadStatus && onLoadStatus(true)}
        onError={() => {
          setOk(false);
          onLoadStatus && onLoadStatus(false);
        }}
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(30,4,8,0.55) 100%)",
        }}
      />
    </div>
  );
}
